import { z } from 'zod';

export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    request_id: z.string()
  })
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/**
 * Payload of a plan-limit rejection (FB-0.2 / CC-8). Carried in the error's
 * `details` so the admin can show usage ("2 of 5 devices") next to the message
 * without parsing text. `limit` is the entitlement name (`device_limit` /
 * `screen_limit`), `used` and `max` are the live counts.
 */
export const LimitErrorDetailsSchema = z.object({
  limit: z.enum(['device_limit', 'screen_limit']),
  used: z.number().int().nonnegative(),
  max: z.number().int().nonnegative()
});
export type LimitErrorDetails = z.infer<typeof LimitErrorDetailsSchema>;

/** The plain, what-to-do message for a limit rejection (FIX_BUILD §6.3). */
export function limitErrorMessage(limit: 'device_limit' | 'screen_limit', used: number, max: number): string {
  return limit === 'device_limit'
    ? `Device limit reached (${used} of ${max}). Remove a device or contact the agency to raise it.`
    : `Screen limit reached (${used} of ${max}). Archive a screen or contact the agency to raise it.`;
}

/**
 * Thrown by not-yet-implemented modules until their task lands.
 *
 * TASK-INTERNAL-000 — this class *defines* the "not implemented" phrase rather
 * than stubbing a task with it, so it carries the sentinel above to satisfy the
 * structure gate; every real stub must still carry its own TASK-* id.
 */
export class NotImplementedError extends Error {
  constructor(taskId: string) {
    super(`NOT IMPLEMENTED: ${taskId}`);
    this.name = 'NotImplementedError';
  }
}
